import type { GuidePage } from '../guides';

const example = [
  { item: 'Labor: 2 painters x 2.5 days (prep 1 day, two coats 1.5 days), loaded hourly cost', amount: '$1,920' },
  { item: 'Paint: 7 gallons wall, 2 gallons ceiling, 1.5 gallons trim, primer for patches', amount: '$520' },
  { item: 'Sundries: tape, plastic, caulk, patching compound, rollers and covers', amount: '$95' },
  { item: 'Travel, setup and daily cleanup across the 3 days', amount: '$160' },
  { item: 'Overhead share and the margin you set for interior work', amount: '$705' },
];

const faq = [
  {
    q: 'Should painters price by the hour, by the room or by the square foot?',
    a: 'Customers like a fixed price per room or per job. Underneath, the most reliable way to get there is to estimate hours from your own production rates for walls, ceilings, trim and doors, then add materials, overhead and margin. Square-foot pricing is a useful check once you know your costs.',
  },
  {
    q: 'How do I estimate how much paint a job needs?',
    a: 'Use the coverage figure on the product\'s data sheet, the paintable area and the number of coats, then round up. Rough or porous surfaces and big color changes use more. Many painters add a little extra for touch-ups.',
  },
  {
    q: 'How should prep be priced?',
    a: 'As its own time, separate from painting. Patching, sanding, caulking, scraping and priming can take as long as the finish coats on older homes. Pricing prep openly also explains your number when a competitor skips it.',
  },
  {
    q: 'Do exterior jobs need a different method?',
    a: 'The same five ingredients apply, but production rates are slower on ladders and lifts, prep is usually heavier, and weather adds days. Price exterior work from exterior production rates, not interior ones.',
  },
];

const page: GuidePage = {
  slug: 'how-to-price-painting',
  path: '/resources/how-to-price-painting',
  primaryKeyword: 'how to price a painting job',
  secondaryKeywords: ['how to estimate a paint job', 'painting pricing guide', 'interior painting cost estimate method'],
  title: 'How to Price a Painting Job, Step by Step | Scopie', // 49
  metaDescription:
    'Price interior and exterior painting from production rates, prep time, paint quantities, sundries, travel, overhead and margin, with a worked example.', // 150
  h1: 'How to price a painting job',
  answerFirst:
    'To price a painting job, estimate the hours from your production rates for walls, ceilings, trim and doors, add prep time on its own, work out paint and sundries from coverage and coats, then add travel, overhead and margin. The interior example below shows how a living room and hall come to a single price.',
  datePublished: '2026-10-07',
  lastReviewed: '2026-10-07',
  schemaType: 'Article',
  blocks: [
    {
      type: 'list',
      id: 'rates',
      h2: 'Know your production rates first',
      style: 'checklist',
      intro: 'Track a few jobs and record, for each painter:',
      items: [
        { text: 'Square feet of wall rolled per hour, per coat.' },
        { text: 'Ceilings per hour, which are slower than walls.' },
        { text: 'Linear feet of trim cut in per hour.' },
        { text: 'Minutes per door, per side, by style.' },
        { text: 'Prep hours per room for typical patching and caulking.' },
      ],
    },
    {
      type: 'sample-quote',
      id: 'example',
      h2: 'Worked example: living room and hallway',
      label: 'Costing an interior repaint (internal numbers)',
      lines: example,
      total: '$3,400',
      totalLabel: 'Price to quote',
      note: 'Illustrative only. Wages, paint prices and production rates vary; use your own.',
    },
    {
      type: 'text',
      id: 'hours',
      h2: 'Turning rates into hours',
      paragraphs: [
        'Measure the paintable wall area room by room (perimeter times height, minus large openings), multiply by the number of coats, and divide by your wall rate. Do the same for ceilings, trim and doors, add the prep hours, and add time to set up and clean up each day. That total is the heart of the price.',
        'Then look at the job itself: high ceilings, stairwells, furniture to move, a family living in the house, or dark colors that need an extra coat all slow the work. Adjust the hours, not just the final number, so you learn from each job.',
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Painting pricing mistakes',
      style: 'mistakes',
      items: [
        { title: 'Using someone else\'s per-square-foot rate', text: 'It carries their crew speed and costs, not yours.' },
        { title: 'Prep buried in the paint price', text: 'You either lose money on old houses or lose bids on new ones.' },
        { title: 'Forgetting sundries', text: 'Tape, plastic and caulk add up across a season.' },
        { title: 'No allowance for color changes', text: 'Deep colors and big changes need extra coats; price them in.' },
      ],
    },
    {
      type: 'text',
      id: 'quote',
      h2: 'From price to estimate',
      paragraphs: [
        'The homeowner sees rooms and surfaces, not your internal costing. In Scopie you save rooms, surfaces and prep as items, build the estimate in minutes, add extras like an accent wall as optional lines, and email it. The homeowner ticks what they want and signs.',
      ],
      links: [{ phrase: 'build the estimate in minutes', href: '/for/painting' }],
    },
    { type: 'faq', id: 'faq', h2: 'Painting pricing questions', items: faq },
  ],
  faq,
  internalLinks: [
    { href: '/for/painting', anchor: 'Scopie for painting contractors', status: 'live' },
    { href: '/templates/painting-estimate-template', anchor: 'painting estimate template', status: 'live' },
    { href: '/resources', anchor: 'All pricing guides', status: 'live' },
  ],
  cta: {
    heading: 'Put your production rates to work',
    text: 'Save your rooms, surfaces and prep items in Scopie, and send each painting estimate the day of the walk-through.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
};

export default page;
